import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, XCircle, DollarSign, PackageX } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * P0 · Record Refund modal (manual refund workflow — 2026-02-16)
 *
 * CollectorUp has NO electronic payment processor integration. Every
 * refund is a bookkeeping entry that captures the fact the admin has
 * returned the money externally (Zelle transfer, cash back, etc.).
 * The modal makes that explicit — copy is always "Record Refund" /
 * "Confirm Refund Sent Externally", never "Refund sent".
 *
 * Three modes:
 *   1. FULL          — refund the remaining refundable balance in one row
 *                       (full_refund adjustment, no line ref).
 *   2. PARTIAL       — admin-entered dollar amount ≤ remaining. Optional
 *                       line pick surfaces `line_item_reference`.
 *   3. ITEM_QUANTITY — admin picks a specific line + qty ≤ remaining
 *                       qty for that line. Records both fields → gives
 *                       future variant-level attribution.
 *
 * Idempotency: a fresh key is minted per modal open. Clicking Confirm
 * multiple times or hitting network retry hits the same key → the
 * ledger short-circuits with `idempotent_hit: true` and the UI shows
 * "already recorded".
 *
 * Refund Only NEVER touches order.status or payment.*.
 */
const REFUND_METHODS = [
  { value: "zelle", label: "Zelle" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card terminal" },
  { value: "cashapp", label: "Cash App" },
  { value: "other", label: "Other" },
];

const REFUND_REASONS = [
  { value: "wrong_items", label: "Wrong items" },
  { value: "price_mistake", label: "Price mistake" },
  { value: "customer_request", label: "Customer requested refund" },
  { value: "out_of_stock", label: "Item out of stock" },
  { value: "duplicate_order", label: "Duplicate order" },
  { value: "other", label: "Other" },
];

const centsFmt = (c) => (Number(c || 0) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
const dollarsToCents = (d) => Math.max(0, Math.round(Number(d || 0) * 100));

function mintIdempotencyKey(prefix, publicId) {
  return `${prefix}-${publicId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function RefundModal({ order, onClose, onSuccess, forceFull = false }) {
  const publicId = order.public_id;
  const sourceId = order.id;
  const [mode, setMode] = useState(forceFull ? "full" : "full");
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [dollarAmount, setDollarAmount] = useState("");
  const [lineRef, setLineRef] = useState("");
  const [qty, setQty] = useState(1);
  const [method, setMethod] = useState("zelle");
  const [reasonKey, setReasonKey] = useState("customer_request");
  const [notes, setNotes] = useState("");
  const [confirmedExternal, setConfirmedExternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const idempotencyKey = useMemo(() => mintIdempotencyKey("refund", publicId), [publicId]);

  useEffect(() => {
    let ok = true;
    api.get(`/admin/sales/adjustments/order-preview?source_type=sticker_order&source_order_id=${sourceId}`)
      .then((r) => { if (ok) setPreview(r.data); })
      .catch((e) => { if (ok) setPreviewErr(formatApiError(e?.response?.data?.detail) || "Failed to load refundable balance"); });
    return () => { ok = false; };
  }, [sourceId]);

  const remaining = preview?.remaining || {};
  const remainingRefundableCents = Number(remaining.customer_total_remaining_refundable || 0);
  const remainingMerchCents = Number(remaining.merchandise_remaining || 0);
  const remainingTaxCents = Number(remaining.tax_remaining || 0);
  const remainingShipCents = Number(remaining.shipping_remaining || 0);
  const lineItems = preview?.line_items || [];
  const selectedLine = lineItems.find((l) => l.line_item_reference === lineRef);

  const alreadyFullyRefunded = preview && remainingRefundableCents === 0;

  const requestedCents = (() => {
    if (mode === "full") return remainingRefundableCents;
    if (mode === "partial") return dollarsToCents(dollarAmount);
    if (mode === "item_quantity" && selectedLine) return Number(selectedLine.unit_price_cents || 0) * Number(qty || 0);
    return 0;
  })();

  const validationErr = (() => {
    if (!preview) return null;
    if (!confirmedExternal) return "Confirm the refund has been returned externally before recording.";
    if (mode === "partial") {
      if (requestedCents <= 0) return "Enter a refund amount greater than $0.";
      if (requestedCents > remainingRefundableCents) return `Amount exceeds remaining refundable balance (${centsFmt(remainingRefundableCents)}).`;
    }
    if (mode === "item_quantity") {
      if (!lineRef) return "Pick a line to refund.";
      if (!qty || Number(qty) <= 0) return "Quantity must be at least 1.";
      if (selectedLine && Number(qty) > Number(selectedLine.remaining_quantity)) {
        return `Quantity exceeds remaining refundable qty (${selectedLine.remaining_quantity}) for ${lineRef}.`;
      }
    }
    if (mode === "full" && remainingRefundableCents === 0) return "Nothing left to refund on this order.";
    return null;
  })();

  const canSubmit = !submitting && !validationErr && preview && !alreadyFullyRefunded;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      let payload = {
        source_type: "sticker_order",
        source_order_id: sourceId,
        payment_method: method,
        reason: (REFUND_REASONS.find((r) => r.value === reasonKey)?.label || "") + (notes ? ` — ${notes.trim()}` : ""),
        idempotency_key: idempotencyKey,
      };
      if (mode === "full") {
        payload = {
          ...payload,
          adjustment_type: "full_refund",
          merchandise_adjustment_cents: remainingMerchCents,
          tax_adjustment_cents: remainingTaxCents,
          shipping_adjustment_cents: remainingShipCents,
        };
      } else if (mode === "partial") {
        // Partial refund cannot exceed remaining refundable and is booked
        // entirely against merchandise unless a specific line was picked.
        payload = {
          ...payload,
          adjustment_type: "partial_refund",
          merchandise_adjustment_cents: requestedCents,
          line_item_reference: lineRef || undefined,
        };
      } else {
        payload = {
          ...payload,
          adjustment_type: "item_quantity_refund",
          line_item_reference: lineRef,
          refund_quantity: Number(qty),
          merchandise_adjustment_cents: Number(selectedLine.unit_price_cents) * Number(qty),
        };
      }
      const r = await api.post("/admin/sales/adjustments", payload);
      if (r.data?.idempotent_hit) {
        toast.success("Refund was already recorded — no duplicate created.");
      } else {
        toast.success(`Refund recorded (${centsFmt(payload.merchandise_adjustment_cents + (payload.tax_adjustment_cents || 0) + (payload.shipping_adjustment_cents || 0))}). Order remains in its current operational status.`);
      }
      onSuccess?.({ idempotent_hit: !!r.data?.idempotent_hit, adjustment: r.data });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to record refund.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" data-testid="refund-modal">
      <div className="w-full max-w-lg bg-white border-2 border-[var(--ink)] rounded-2xl p-6 shadow-[8px_8px_0_var(--ink)] max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl" data-testid="refund-title">Record Refund · {publicId}</h3>
          <button className="text-xs uppercase font-bold" onClick={onClose} data-testid="refund-close">Close</button>
        </div>
        <p className="text-xs text-[var(--smoke)] mt-1">
          CollectorUp does not electronically return money. Use this to record a refund you have already sent externally.
        </p>

        {previewErr && <div className="mt-4 text-sm text-red-800" data-testid="refund-preview-error">{previewErr}</div>}

        {preview && (
          <>
            <div className="mt-4 rounded-md border-2 border-[var(--ink)] p-3 text-xs bg-[var(--paper)]"
                 data-testid="refund-remaining">
              <div className="font-mono">Refundable remaining:</div>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <div><div className="uppercase text-[9px]">Merchandise</div><div className="font-mono">{centsFmt(remainingMerchCents)}</div></div>
                <div><div className="uppercase text-[9px]">Tax</div><div className="font-mono">{centsFmt(remainingTaxCents)}</div></div>
                <div><div className="uppercase text-[9px]">Shipping</div><div className="font-mono">{centsFmt(remainingShipCents)}</div></div>
              </div>
              <div className="mt-1"><b>Total remaining refundable: {centsFmt(remainingRefundableCents)}</b></div>
            </div>

            {alreadyFullyRefunded && (
              <div className="mt-3 text-xs text-yellow-800 border border-yellow-500 rounded-md p-2"
                   data-testid="refund-already-full">
                This order has already been fully refunded. No further refund can be recorded.
              </div>
            )}

            {!alreadyFullyRefunded && !forceFull && (
              <div className="mt-4 flex gap-2 flex-wrap" data-testid="refund-mode-toggle">
                <button className={`text-xs px-2 py-1 rounded-md border-2 ${mode === "full" ? "bg-[var(--ink)] text-white" : "border-[var(--ink)]"}`}
                        onClick={() => setMode("full")} data-testid="refund-mode-full">Full order refund</button>
                <button className={`text-xs px-2 py-1 rounded-md border-2 ${mode === "partial" ? "bg-[var(--ink)] text-white" : "border-[var(--ink)]"}`}
                        onClick={() => setMode("partial")} data-testid="refund-mode-partial">Partial dollar refund</button>
                <button className={`text-xs px-2 py-1 rounded-md border-2 ${mode === "item_quantity" ? "bg-[var(--ink)] text-white" : "border-[var(--ink)]"}`}
                        onClick={() => setMode("item_quantity")} data-testid="refund-mode-item">Item / quantity refund</button>
              </div>
            )}

            {mode === "full" && !alreadyFullyRefunded && (
              <div className="mt-3 text-xs" data-testid="refund-full-summary">
                Records a single <b>full_refund</b> for the remaining <b>{centsFmt(remainingRefundableCents)}</b>. Order operational status is unchanged.
              </div>
            )}

            {mode === "partial" && (
              <div className="mt-3 space-y-2" data-testid="refund-partial-panel">
                <label className="text-[10px] uppercase font-bold text-[var(--smoke)]">Refund amount (USD)</label>
                <input type="number" step="0.01" min="0" value={dollarAmount}
                       onChange={(e) => setDollarAmount(e.target.value)}
                       className="w-full border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm font-mono"
                       placeholder="e.g. 5.00"
                       data-testid="refund-partial-amount" />
                <label className="text-[10px] uppercase font-bold text-[var(--smoke)]">Attribute to line (optional)</label>
                <select value={lineRef} onChange={(e) => setLineRef(e.target.value)}
                        className="w-full border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm"
                        data-testid="refund-partial-line">
                  <option value="">— aggregate merchandise —</option>
                  {lineItems.map((l) => (
                    <option key={l.line_item_reference} value={l.line_item_reference}>
                      {l.line_item_reference} · {l.remaining_quantity} left · {centsFmt(l.unit_price_cents)} ea
                    </option>
                  ))}
                </select>
              </div>
            )}

            {mode === "item_quantity" && (
              <div className="mt-3 space-y-2" data-testid="refund-item-panel">
                <label className="text-[10px] uppercase font-bold text-[var(--smoke)]">Line to refund</label>
                <select value={lineRef} onChange={(e) => setLineRef(e.target.value)}
                        className="w-full border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm"
                        data-testid="refund-item-line">
                  <option value="">— select line —</option>
                  {lineItems.filter((l) => l.remaining_quantity > 0).map((l) => (
                    <option key={l.line_item_reference} value={l.line_item_reference}>
                      {l.line_item_reference} · {l.remaining_quantity} refundable · {centsFmt(l.unit_price_cents)} ea
                    </option>
                  ))}
                </select>
                <label className="text-[10px] uppercase font-bold text-[var(--smoke)]">Quantity</label>
                <input type="number" min="1" step="1" value={qty}
                       onChange={(e) => setQty(e.target.value)}
                       className="w-full border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm font-mono"
                       data-testid="refund-item-qty" />
                {selectedLine && (
                  <div className="text-xs">
                    Refund amount: <b data-testid="refund-item-computed">{centsFmt(requestedCents)}</b>
                    &nbsp;· {selectedLine.remaining_quantity} refundable
                  </div>
                )}
              </div>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-[var(--smoke)]">Return method</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)}
                        className="w-full border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm"
                        data-testid="refund-method">
                  {REFUND_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-[var(--smoke)]">Reason</label>
                <select value={reasonKey} onChange={(e) => setReasonKey(e.target.value)}
                        className="w-full border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm"
                        data-testid="refund-reason">
                  {REFUND_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <label className="text-[10px] uppercase font-bold text-[var(--smoke)] mt-2 block">Note (optional)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="w-full border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm"
                      placeholder="Any additional context for the audit log."
                      data-testid="refund-notes" />

            <label className="mt-3 flex items-start gap-2 text-xs cursor-pointer"
                   data-testid="refund-external-confirm-wrap">
              <input type="checkbox" checked={confirmedExternal}
                     onChange={(e) => setConfirmedExternal(e.target.checked)}
                     className="mt-0.5"
                     data-testid="refund-external-confirm" />
              <span>I confirm the refund has been returned to the customer externally.</span>
            </label>

            {validationErr && (
              <div className="mt-2 text-xs text-red-800" data-testid="refund-validation-error">{validationErr}</div>
            )}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button className="btn-ghost text-sm" onClick={onClose} data-testid="refund-cancel">Cancel</button>
              <button
                className="btn-red text-sm"
                disabled={!canSubmit}
                onClick={submit}
                data-testid="refund-confirm"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />}
                Record Refund
              </button>
            </div>
            <div className="mt-3 text-[11px] text-[var(--smoke)] italic" data-testid="refund-post-note">
              After successful recording, the order remains in its current operational status.
              If the original paid order needs merchandise or pricing corrections, use Refund &amp; Cancel and create a corrected
              replacement order. Paid order merchandise lines are currently frozen.
            </div>
          </>
        )}
      </div>
    </div>
  );
}


/**
 * P0 · Refund & Cancel composite action (2026-02-16)
 *
 * Runs the FULL remaining refund first, then the cancel. Idempotent
 * refund key is generated once so retrying the cancel step after a
 * failure NEVER duplicates the refund. If the order's remaining
 * refundable balance is already $0 (already fully refunded), the modal
 * skips the refund call and only cancels.
 */
export function RefundAndCancelModal({ order, onClose, onSuccess }) {
  const publicId = order.public_id;
  const sourceId = order.id;
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [reasonKey, setReasonKey] = useState("customer_request");
  const [method, setMethod] = useState("zelle");
  const [notes, setNotes] = useState("");
  const [confirmedExternal, setConfirmedExternal] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | refunding | refunded | cancelling | done | cancel_failed
  const [refundResult, setRefundResult] = useState(null);
  const [cancelErr, setCancelErr] = useState(null);
  const refundKey = useMemo(() => mintIdempotencyKey("refund-and-cancel", publicId), [publicId]);

  useEffect(() => {
    let ok = true;
    api.get(`/admin/sales/adjustments/order-preview?source_type=sticker_order&source_order_id=${sourceId}`)
      .then((r) => { if (ok) setPreview(r.data); })
      .catch((e) => { if (ok) setPreviewErr(formatApiError(e?.response?.data?.detail) || "Failed to load refundable balance"); });
    return () => { ok = false; };
  }, [sourceId]);

  const remaining = preview?.remaining || {};
  const remainingRefundableCents = Number(remaining.customer_total_remaining_refundable || 0);
  const remainingMerchCents = Number(remaining.merchandise_remaining || 0);
  const remainingTaxCents = Number(remaining.tax_remaining || 0);
  const remainingShipCents = Number(remaining.shipping_remaining || 0);
  const alreadyFullyRefunded = preview && remainingRefundableCents === 0;

  const canSubmit = preview && confirmedExternal && (phase === "idle" || phase === "cancel_failed");

  const doCancel = async () => {
    setPhase("cancelling");
    setCancelErr(null);
    try {
      const reasonLabel = REFUND_REASONS.find((r) => r.value === reasonKey)?.label || "Refunded and cancelled";
      await api.post(`/admin/orders/${publicId}/status`, {
        target: "cancelled",
        reason: `${reasonLabel} — Refund & Cancel`,
      });
      setPhase("done");
      toast.success(alreadyFullyRefunded
        ? "Order cancelled (already fully refunded — no new refund recorded)."
        : "Refund recorded and order cancelled.");
      onSuccess?.({ refund: refundResult, cancelled: true });
    } catch (e) {
      setCancelErr(formatApiError(e?.response?.data?.detail) || "Cancellation failed.");
      setPhase("cancel_failed");
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    // Skip refund step entirely when nothing left to refund.
    if (alreadyFullyRefunded) {
      await doCancel();
      return;
    }
    setPhase("refunding");
    try {
      const payload = {
        source_type: "sticker_order",
        source_order_id: sourceId,
        adjustment_type: "full_refund",
        merchandise_adjustment_cents: remainingMerchCents,
        tax_adjustment_cents: remainingTaxCents,
        shipping_adjustment_cents: remainingShipCents,
        payment_method: method,
        reason: (REFUND_REASONS.find((r) => r.value === reasonKey)?.label || "") + (notes ? ` — ${notes.trim()}` : ""),
        idempotency_key: refundKey,   // stable across retries
      };
      const r = await api.post("/admin/sales/adjustments", payload);
      setRefundResult(r.data);
      setPhase("refunded");
      await doCancel();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to record refund.");
      setPhase("idle");
    }
  };

  const primaryLabel = (() => {
    if (phase === "refunding") return "Recording refund…";
    if (phase === "cancelling") return "Cancelling…";
    if (phase === "cancel_failed") return "Retry Cancel";
    if (phase === "done") return "Done";
    if (alreadyFullyRefunded) return "Cancel Refunded Order";
    return "Refund & Cancel";
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog"
         data-testid="refund-and-cancel-modal">
      <div className="w-full max-w-lg bg-white border-2 border-[var(--ink)] rounded-2xl p-6 shadow-[8px_8px_0_var(--ink)]">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl" data-testid="rac-title">Refund &amp; Cancel · {publicId}</h3>
          <button className="text-xs uppercase font-bold" onClick={onClose}
                  data-testid="rac-close"
                  disabled={phase === "refunding" || phase === "cancelling"}>Close</button>
        </div>
        <p className="text-xs text-[var(--smoke)] mt-1">
          This action refunds the remaining refundable balance and then cancels the order. Both events are audit-preserved.
        </p>

        {previewErr && <div className="mt-4 text-sm text-red-800" data-testid="rac-preview-error">{previewErr}</div>}

        {preview && (
          <>
            <div className="mt-4 rounded-md border-2 border-[var(--ink)] p-3 text-xs bg-[var(--paper)]"
                 data-testid="rac-remaining">
              <div><b>Remaining refundable: {centsFmt(remainingRefundableCents)}</b></div>
              <div className="grid grid-cols-3 gap-2 mt-1 text-[11px]">
                <div>Merch: {centsFmt(remainingMerchCents)}</div>
                <div>Tax: {centsFmt(remainingTaxCents)}</div>
                <div>Ship: {centsFmt(remainingShipCents)}</div>
              </div>
            </div>

            {alreadyFullyRefunded && (
              <div className="mt-3 text-xs text-yellow-800 border border-yellow-500 rounded-md p-2"
                   data-testid="rac-already-refunded">
                Order is already fully refunded. This action will ONLY cancel the order — no new refund will be recorded.
              </div>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-[var(--smoke)]">Return method</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)}
                        className="w-full border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm"
                        data-testid="rac-method"
                        disabled={alreadyFullyRefunded}>
                  {REFUND_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-[var(--smoke)]">Reason</label>
                <select value={reasonKey} onChange={(e) => setReasonKey(e.target.value)}
                        className="w-full border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm"
                        data-testid="rac-reason">
                  {REFUND_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <label className="text-[10px] uppercase font-bold text-[var(--smoke)] mt-2 block">Note (optional)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="w-full border-2 border-[var(--ink)] rounded-md px-2 py-1 text-sm"
                      placeholder="Any additional context for the audit log."
                      data-testid="rac-notes" />

            {!alreadyFullyRefunded && (
              <label className="mt-3 flex items-start gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={confirmedExternal}
                       onChange={(e) => setConfirmedExternal(e.target.checked)}
                       className="mt-0.5"
                       data-testid="rac-external-confirm" />
                <span>I confirm the refund has been returned to the customer externally.</span>
              </label>
            )}

            {phase === "refunded" && (
              <div className="mt-3 text-xs text-emerald-800" data-testid="rac-refund-recorded">
                ✓ Refund recorded. Cancelling order…
              </div>
            )}
            {phase === "cancel_failed" && (
              <div className="mt-3 text-xs text-red-800 border border-red-500 rounded-md p-2"
                   data-testid="rac-cancel-failed">
                Refund was recorded successfully, but cancellation failed: {cancelErr || "unknown error"}.
                Click <b>Retry Cancel</b> — the refund will NOT be recorded again.
              </div>
            )}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button className="btn-ghost text-sm" onClick={onClose}
                      disabled={phase === "refunding" || phase === "cancelling"}
                      data-testid="rac-close-btn">Close</button>
              <button
                className="btn-red text-sm"
                disabled={
                  phase === "refunding" || phase === "cancelling" || phase === "done"
                  || (!confirmedExternal && !alreadyFullyRefunded)
                }
                onClick={phase === "cancel_failed" ? doCancel : submit}
                data-testid="rac-confirm"
              >
                {(phase === "refunding" || phase === "cancelling")
                  ? <Loader2 size={14} className="animate-spin" />
                  : (phase === "cancel_failed" ? <XCircle size={14} /> : <PackageX size={14} />)}
                {primaryLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
